import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import CookieConsentBanner from "./components/CookieConsentBanner";
import CookiesPage from "./components/CookiesPage";
import DisclaimerModal from "./components/DisclaimerModal";
import DisclaimerPage from "./components/DisclaimerPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import SiteFooter from "./components/SiteFooter";
import TermsOfUsePage from "./components/TermsOfUsePage";
import PlanInputsSection from "./components/PlanInputsSection";
import ResultsPanel from "./components/ResultsPanel";
import { INITIAL_INPUTS } from "./constants/defaults";
import { LOAN_PLANS, LOAN_PLAN_LABELS } from "./constants/loanPlans";
import { useLoanCalculator } from "./hooks/useLoanCalculator";
import { trackAnalyticsEvent } from "./utils/analytics";
import { resetConsentStatus } from "./utils/consent";
import { acceptDisclaimer, hasAcceptedCurrentDisclaimer } from "./utils/disclaimerConsent";
import { buildCalculationPayload } from "./utils/loanPayload";
import { isValidCurrencyInput } from "./utils/numericInput";
import { validateLoanForm } from "./utils/validation";

const MOBILE_TOOLTIP_QUERY = "(max-width: 700px)";
const TOOLTIP_EDGE_MARGIN = 12;
const TOOLTIP_VERTICAL_GAP = 8;
const TOOLTIP_ARROW_PADDING = 16;
const TOOLTIP_MAX_WIDTH = 280;

const clearTooltipPosition = (tooltip) => {
  tooltip.style.removeProperty("left");
  tooltip.style.removeProperty("top");
  tooltip.style.removeProperty("right");
  tooltip.style.removeProperty("bottom");
  tooltip.style.removeProperty("transform");
  tooltip.style.removeProperty("width");
  tooltip.style.removeProperty("max-width");
  tooltip.style.removeProperty("position");
  tooltip.style.removeProperty("--tooltip-arrow-offset");
  tooltip.removeAttribute("data-placement");
};

const positionTooltip = (infoBtn) => {
  const tooltip = infoBtn.querySelector(".info-tooltip");

  if (!tooltip) {
    return;
  }

  if (!window.matchMedia(MOBILE_TOOLTIP_QUERY).matches) {
    clearTooltipPosition(tooltip);
    return;
  }

  const previousDisplay = tooltip.style.display;
  const previousVisibility = tooltip.style.visibility;

  tooltip.style.display = "block";
  tooltip.style.visibility = "hidden";
  tooltip.style.position = "fixed";
  tooltip.style.left = "0px";
  tooltip.style.top = "0px";
  tooltip.style.right = "auto";
  tooltip.style.bottom = "auto";
  tooltip.style.transform = "none";
  tooltip.style.width = `${Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - TOOLTIP_EDGE_MARGIN * 2)}px`;
  tooltip.style.maxWidth = `${window.innerWidth - TOOLTIP_EDGE_MARGIN * 2}px`;

  const buttonRect = infoBtn.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const maxWidth = window.innerWidth - TOOLTIP_EDGE_MARGIN * 2;
  const width = Math.min(tooltipRect.width, maxWidth);
  const centeredLeft = buttonRect.left + buttonRect.width / 2 - width / 2;
  const left = Math.max(
    TOOLTIP_EDGE_MARGIN,
    Math.min(centeredLeft, window.innerWidth - width - TOOLTIP_EDGE_MARGIN)
  );

  const spaceAbove = buttonRect.top;
  const spaceBelow = window.innerHeight - buttonRect.bottom;
  const needsBelowPlacement =
    spaceAbove < tooltipRect.height + TOOLTIP_VERTICAL_GAP + TOOLTIP_EDGE_MARGIN &&
    spaceBelow > spaceAbove;

  let top = needsBelowPlacement
    ? buttonRect.bottom + TOOLTIP_VERTICAL_GAP
    : buttonRect.top - tooltipRect.height - TOOLTIP_VERTICAL_GAP;

  top = Math.max(
    TOOLTIP_EDGE_MARGIN,
    Math.min(top, window.innerHeight - tooltipRect.height - TOOLTIP_EDGE_MARGIN)
  );

  const arrowOffset = Math.max(
    TOOLTIP_ARROW_PADDING,
    Math.min(width - TOOLTIP_ARROW_PADDING, buttonRect.left + buttonRect.width / 2 - left)
  );

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
  tooltip.style.width = `${width}px`;
  tooltip.style.maxWidth = `${width}px`;
  tooltip.style.setProperty("--tooltip-arrow-offset", `${arrowOffset}px`);
  tooltip.setAttribute("data-placement", needsBelowPlacement ? "bottom" : "top");
  tooltip.style.display = previousDisplay;
  tooltip.style.visibility = previousVisibility;
};

const PLAN_INFORMATION = [
  {
    key: "plan1",
    paymentRate: "9%",
    threshold: "£26900",
    writeOff: "25 years",
    assumedWipe: true,
    interestNote:
      "Interest rate charged is either the Retail Price Index or the Bank of England base rate plus 1%, " +
      "whichever is lower, however in this model we simplify to just use the RPI which we assume to be constant.",
    interestUrl: "https://www.gov.uk/guidance/how-interest-is-calculated-plan-1",
  },
  {
    key: "plan2",
    paymentRate: "9%",
    threshold: "£29385",
    writeOff: "30 years",
    assumedWipe: false,
    interestNote:
      "Interest rate usually calculated based on income as RPI + up to 3%. This is typically done each year in September " +
      "using the RPI from the previous March but in this model we simplify to calculate interest rate in January. " +
      "We assume RPI to be constant.",
    interestUrl: "https://www.gov.uk/guidance/how-interest-is-calculated-plan-2",
  },
  {
    key: "postgraduate",
    paymentRate: "6%",
    threshold: "£21000",
    writeOff: "30 years",
    assumedWipe: false,
    interestNote:
      "Interest rate usually set in September using the previous March RPI + 3%. We assume RPI to be constant.",
    interestUrl: "https://www.gov.uk/guidance/how-interest-is-calculated-postgraduate-loan",
  },
  {
    key: "plan4",
    paymentRate: "9%",
    threshold: "£33795",
    writeOff: "30 years",
    assumedWipe: true,
    interestNote:
      "Interest rate charged is either the Retail Price Index or the Bank of England base rate plus 1%, " +
      "whichever is lower, however in this model we simplify to just use the RPI which we assume to be constant.",
    interestUrl: "https://www.gov.uk/guidance/how-interest-is-calculated-plan-4",
  },
  {
    key: "plan5",
    paymentRate: "9%",
    threshold: "£25000",
    writeOff: "40 years",
    assumedWipe: false,
    interestNote:
      "Interest rate usually set in September using the previous March RPI. We assume RPI to be constant.",
    interestUrl: "https://www.gov.uk/guidance/how-interest-is-calculated-plan-5",
  },
];

function App() {
  const isCookiesPage = window.location.pathname === "/cookies";
  const isPrivacyPage = window.location.pathname === "/privacy";
  const isTermsPage = window.location.pathname === "/terms";
  const isDisclaimerPage = window.location.pathname === "/disclaimer";

  const formatInterestNote = (interestNote) =>
    interestNote
      .replace(/^interest rate\s+/i, "")
      .replace(/^is\s+/i, "");

  const [inputs, setInputs] = useState(INITIAL_INPUTS);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [planDetails, setPlanDetails] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [overpaymentExpanded, setOverpaymentExpanded] = useState({});
  const [editedIncomes, setEditedIncomes] = useState({});
  const [expandedMonthExplanations, setExpandedMonthExplanations] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [isDisclaimerAccepted, setIsDisclaimerAccepted] = useState(hasAcceptedCurrentDisclaimer());

  const { result, setResult, selectedResult, setSelectedResult, calculate } = useLoanCalculator();

  const selectedPlanLabels = useMemo(() => {
    return selectedPlans.reduce((acc, planKey) => {
      acc[planKey] = LOAN_PLAN_LABELS[planKey] || planKey;
      return acc;
    }, {});
  }, [selectedPlans]);

  const toggleExpand = (planKey, idx) => {
    setExpandedRows((prev) => ({
      ...prev,
      [planKey]: {
        ...prev[planKey],
        [idx]: !prev[planKey]?.[idx],
      },
    }));
  };

  const toggleMonthExplain = (planKey, yearIdx, monthIdx) => {
    setExpandedMonthExplanations((prev) => ({
      ...prev,
      [`${planKey}-${yearIdx}-${monthIdx}`]: !prev[`${planKey}-${yearIdx}-${monthIdx}`],
    }));
  };

  const handleChange = (event) => {
    const { name, value, type } = event.target;
    setInputs((prev) => ({
      ...prev,
      [name]: type === "number" || type === "range"
        ? (value === "" ? "" : parseFloat(value))
        : value,
    }));
  };

  const handlePlanCheckbox = (planKey) => {
    setSelectedPlans((prev) =>
      prev.includes(planKey)
        ? prev.filter((key) => key !== planKey)
        : [...prev, planKey]
    );

    setPlanDetails((prev) => {
      if (!selectedPlans.includes(planKey)) return prev;
      const newDetails = { ...prev };
      delete newDetails[planKey];
      return newDetails;
    });
  };

  const handlePlanDetailChange = (planKey, field, value) => {
    setPlanDetails((prev) => ({
      ...prev,
      [planKey]: {
        ...prev[planKey],
        [field]: value,
      },
    }));
  };

  const buildPayload = (editedIncomesParam = editedIncomes) =>
    buildCalculationPayload({
      inputs,
      selectedPlans,
      planDetails,
      editedIncomes: editedIncomesParam,
    });

  const openCookieSettings = () => {
    resetConsentStatus();
  };

  const handleDisclaimerAccept = () => {
    const acceptanceRecord = acceptDisclaimer();
    setIsDisclaimerAccepted(true);

    trackAnalyticsEvent("disclaimer_accepted", {
      disclaimer_version: acceptanceRecord.disclaimerVersion,
    });
  };

  const handleSubmit = async () => {
    if (!isDisclaimerAccepted) {
      return;
    }

    trackAnalyticsEvent("calculate_click", {
      selected_plan_count: selectedPlans.length,
      has_overpayment_selected: selectedPlans.some(
        (planKey) => Number(planDetails?.[planKey]?.overpayment_amount) > 0
      ),
    });

    const errors = validateLoanForm({
      selectedPlans,
      planDetails,
      inputs,
      loanPlanLabels: LOAN_PLAN_LABELS,
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      setResult(null);
      setSelectedResult("");
      setExpandedRows({});
      setExpandedMonthExplanations({});
      return;
    }

    setValidationErrors([]);

    try {
      setEditedIncomes({});
      await calculate(buildPayload({}), true);
      setExpandedRows({});
      setExpandedMonthExplanations({});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error during calculation:", error);
    }
  };

  const handleIncomeEdit = async (editedIncomesParam = editedIncomes) => {
    try {
      await calculate(buildPayload(editedIncomesParam));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error during calculation:", error);
    }
  };

  const handleResetIncomes = () => {
    setEditedIncomes({});
    setTimeout(() => handleIncomeEdit({}), 0);
  };

  const preventWheelNumberInputChange = (event) => {
    event.currentTarget.blur();
  };

  const preventNegativeSignInput = (event) => {
    if (event.key === "-" || event.key === "Subtract") {
      event.preventDefault();
    }
  };

  useEffect(() => {
    selectedPlans.forEach((planKey) => {
      if (!planDetails[planKey]) {
        handlePlanDetailChange(planKey, "overpayment_type", "monthly");
        handlePlanDetailChange(planKey, "overpayment_amount", "");
        handlePlanDetailChange(planKey, "overpayment_lump_sum_whole_thing", false);
      }
    });
  }, [selectedPlans, planDetails]);

  useEffect(() => {
    const getInfoButtonFromEvent = (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return null;
      }

      return target.closest(".info-btn");
    };

    const handleTooltipOpen = (event) => {
      const infoButton = getInfoButtonFromEvent(event);

      if (!infoButton) {
        return;
      }

      positionTooltip(infoButton);
    };

    const refreshVisibleTooltips = () => {
      const visibleButtons = document.querySelectorAll(".info-btn:hover, .info-btn:focus-within");

      visibleButtons.forEach((button) => {
        positionTooltip(button);
      });
    };

    document.addEventListener("mouseover", handleTooltipOpen);
    document.addEventListener("focusin", handleTooltipOpen);
    document.addEventListener("touchstart", handleTooltipOpen, { passive: true });

    window.addEventListener("resize", refreshVisibleTooltips);
    window.addEventListener("orientationchange", refreshVisibleTooltips);
    window.addEventListener("scroll", refreshVisibleTooltips, true);

    return () => {
      document.removeEventListener("mouseover", handleTooltipOpen);
      document.removeEventListener("focusin", handleTooltipOpen);
      document.removeEventListener("touchstart", handleTooltipOpen);

      window.removeEventListener("resize", refreshVisibleTooltips);
      window.removeEventListener("orientationchange", refreshVisibleTooltips);
      window.removeEventListener("scroll", refreshVisibleTooltips, true);
    };
  }, []);

  if (isCookiesPage) {
    return (
      <>
        <CookiesPage onOpenCookieSettings={openCookieSettings} />
        <SiteFooter onOpenCookieSettings={openCookieSettings} />
        <CookieConsentBanner />
      </>
    );
  }

  if (isPrivacyPage) {
    return (
      <>
        <PrivacyPolicyPage onOpenCookieSettings={openCookieSettings} />
        <SiteFooter onOpenCookieSettings={openCookieSettings} />
        <CookieConsentBanner />
      </>
    );
  }

  if (isTermsPage) {
    return (
      <>
        <TermsOfUsePage />
        <SiteFooter onOpenCookieSettings={openCookieSettings} />
        <CookieConsentBanner />
      </>
    );
  }

  if (isDisclaimerPage) {
    return (
      <>
        <DisclaimerPage />
        <SiteFooter onOpenCookieSettings={openCookieSettings} />
        <CookieConsentBanner />
      </>
    );
  }

  return (
    <div className="container">
      <div className="app-header">
        <h1 className="main-title">Student Loan Amount Modeller</h1>
        <p className="app-intro">
          Forecast your UK student loan balance and repayments.
        </p>
        <p className="app-intro">
          Compare the effects of overpaying.
        </p>
        <p className="app-intro">
          Get stuck into the maths.
        </p>
        <img src="/logo.png" alt="Student Loan Modeller Logo" className="app-logo" />
      </div>

      <form
        className="inputs-form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div className="input-group input-group-emphasis-gap">
          <label className="input-label">
            Select Loan Plans
            <span className="info-btn" tabIndex={0} aria-label="How to check your repayment plan">
              <span className="info-icon">i</span>
              <span className="info-tooltip">
                Check which loan plan you are on. {" "}
                <a
                  href="https://www.gov.uk/repaying-your-student-loan/which-repayment-plan-you-are-on"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Repaying your student loan: Which repayment plan you&apos;re on - GOV.UK
                </a>
              </span>
            </span>
          </label>
          <div className="loan-plan-checkbox-group">
            {LOAN_PLANS.map((plan) => {
              const nonPgSelected = selectedPlans.find((k) => k !== "postgraduate");
              const isDisabled =
                plan.key !== "postgraduate" &&
                !!nonPgSelected &&
                nonPgSelected !== plan.key;
              return (
                <label
                  key={plan.key}
                  className={`loan-plan-checkbox-label plan-checkbox-item${isDisabled ? " plan-checkbox-disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPlans.includes(plan.key)}
                    onChange={() => handlePlanCheckbox(plan.key)}
                    disabled={isDisabled}
                  />
                  {plan.label}
                </label>
              );
            })}
          </div>
        </div>

        <PlanInputsSection
          selectedPlans={selectedPlans}
          selectedPlanLabels={selectedPlanLabels}
          planDetails={planDetails}
          overpaymentExpanded={overpaymentExpanded}
          onPlanDetailChange={handlePlanDetailChange}
          onToggleOverpayment={(planKey) =>
            setOverpaymentExpanded((prev) => ({
              ...prev,
              [planKey]: !prev[planKey],
            }))
          }
        />

        <div className="input-group input-group-emphasis-gap">
          <label className="input-label">
            Current Total Gross Annual Income (£)
            <span className="info-btn" tabIndex={0} aria-label="What is current total gross annual income?">
              <span className="info-icon">i</span>
              <span className="info-tooltip">
                Your expected annual gross pay from your job, before tax and NI deductions
                (but after pension contributions). Can be found on your payslip.
                This starting value is used to project all future repayments.<br></br><br></br>
                See the following link if you have more than one employer, are self employed or complete a Self Assessment tax return  {" "}
                <a
                  href="https://www.gov.uk/repaying-your-student-loan/what-you-pay"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Repaying your student loan: What you pay - GOV.UK
                </a>
                <br></br><br></br>
                If your {" "}
                <a
                  href="https://assets.publishing.service.gov.uk/media/6a0adbb3279ebb7d24f8f389/adm-Ch-H5-unearned-income.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  unearned income
                </a>
                {" "} is over £2000 include it in this value otherwise exclude it {" "}
                <a
                  href="https://www.gov.uk/hmrc-internal-manuals/collection-of-student-loans-manual/cslm16035"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  SL repayments: calculation of loan repayments: unearned income - HMRC internal manual - GOV.UK
                </a>
                
              </span>
            </span>
          </label>
          <input
            className="styled-input"
            type="text"
            inputMode="decimal"
            name="starting_income"
            step="0.01"
            min="0"
            maxLength={12}
            value={inputs.starting_income}
            onChange={(event) => {
              const { value } = event.target;
              if (isValidCurrencyInput(value, { maxLength: 12 })) {
                setInputs((prev) => ({
                  ...prev,
                  starting_income: value,
                }));
              }
            }}
            onKeyDown={preventNegativeSignInput}
            onWheel={preventWheelNumberInputChange}
          />
        </div>
        <div className="input-group input-group-emphasis-gap">
          <label className="input-label">
            Estimated Annual Income Growth Rate (%)
            <span className="info-btn" tabIndex={0} aria-label="What is estimated annual income growth rate?">
              <span className="info-icon">i</span>
              <span className="info-tooltip">
                How much your gross income is expected to increase each year.
              </span>
            </span>
          </label>
          <div className="slider-wrapper">
            <input
              className="styled-slider"
              type="range"
              name="income_growth_rate"
              min={0}
              max={0.1}
              step={0.001}
              value={inputs.income_growth_rate}
              onChange={handleChange}
            />
            <span className="slider-value">{(inputs.income_growth_rate * 100).toFixed(1)}%</span>
          </div>
        </div>
        <h2
          className="input-label overpayment-heading"
          onClick={() => setAdvancedExpanded((prev) => !prev)}
        >
          Advanced {advancedExpanded ? "▲" : "▼"}
        </h2>

        {advancedExpanded && (
          <>
            <div className="input-group">
              <label className="input-label">
                Inflation RPI (%)
                <span className="info-btn" tabIndex={0} aria-label="What is inflation RPI?">
                  <span className="info-icon">i</span>
                  <span className="info-tooltip">
                    The assumed annual Retail Price Index (RPI) inflation rate.
                    This is used to calculate the interest rate of the loans.
                    The default of 4.1% reflects the applicable RPI used by the government 
                    to calculate interest rates for the period 1 September 2026 to 31 August 2027.
                  </span>
                </span>
              </label>
              <div className="slider-wrapper">
                <input
                  className="styled-slider"
                  type="range"
                  name="rpi"
                  min={0}
                  max={0.1}
                  step={0.001}
                  value={inputs.rpi}
                  onChange={handleChange}
                />
                <span className="slider-value">{(inputs.rpi * 100).toFixed(1)}%</span>
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">
                Annual Threshold Increase (%)
                <span className="info-btn" tabIndex={0} aria-label="What is annual threshold increase?">
                  <span className="info-icon">i</span>
                  <span className="info-tooltip">
                    The estimated average yearly uplift applied by the government to repayment thresholds.
                    Note repayment thresholds are often frozen for multiple years so it is challenging to predict future increases
                    and different plans increase at different rates.
                    The default of 2% reflects the approximate average annual increase in the Plan 2 repayment threshold over the last 8 years (April 2018 - April 2026).
                  </span>
                </span>
              </label>
              <div className="slider-wrapper">
                <input
                  className="styled-slider"
                  type="range"
                  name="annual_threshold_increase"
                  min={0}
                  max={0.05}
                  step={0.001}
                  value={inputs.annual_threshold_increase}
                  onChange={handleChange}
                />
                <span className="slider-value">{(inputs.annual_threshold_increase * 100).toFixed(1)}%</span>
              </div>
            </div>
          </>
        )}

        {validationErrors.length > 0 && (
          <div className="validation-errors">
            <ul>
              {validationErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <button className="calculate-btn" type="submit">
          Calculate
        </button>
      </form>

      <ResultsPanel
        result={result}
        selectedResult={selectedResult}
        setSelectedResult={setSelectedResult}
        loanPlanLabels={LOAN_PLAN_LABELS}
        planDetails={planDetails}
        renderTableProps={{
          expandedRows,
          toggleExpand,
          editedIncomes,
          setEditedIncomes,
          handleIncomeEdit,
          expandedMonthExplanations,
          toggleMonthExplain,
          onResetIncomes: handleResetIncomes,
        }}
      />

      <div className="post-calculate-info-panels">
        <details className="assumptions-panel">
          <summary className="assumptions-title">
            <span>Assumptions and Key Information</span>
            <span className="valid-as-of-inline">Valid as of 19th October 2026</span>
          </summary>
          <div className="assumptions-content">
            <p>
              These outputs are simplified estimates and not financial advice. Key model assumptions are listed
              below.
            </p>
            <ul>
              <li>
                This tool is intended for people who have finished their university course, or are about to finish,
                and assumes no further student finance payments will be made to you.
              </li>
              <li>
                This tool is intended for people who studied in the UK and currently live in the UK. 
                Moving abroad may affect your repayments and this tool does not account for that.
              </li>
              <li>Annual repayment thresholds are increased in January each year by a constant percentage for simpler year-by-year modelling.</li>
              <li>Annual income growth is applied in January each year.</li>
              <li>
                Plan 2 loans with a course completion date in 2026 are assumed to move directly onto the
                Variable Interest Rate.
              </li>
              <li>RPI is treated as constant for the full projection period.</li>
              <li>
                Repayments are assumed to begin from the April after Course Finish Date.
                {" "}
                <a
                  href="https://www.gov.uk/repaying-your-student-loan/when-you-start-repaying"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>
              <li>Course Finish Date must be between January 2009 and December 2026.</li>
            </ul>

            <h3>Key Information</h3>
            <ul>
              <li>
                <strong>
                  This tool is not suitable if you are on more than one plan type that is not a
                  Postgraduate Loan.
                </strong>{" "}
                In this case, repayments are usually 9% of income over the lowest threshold among your plans,
                and this model does not account for that.
              </li>
              <li>
                Repaying (all plan types):
                <a
                  href="https://www.gov.uk/repaying-your-student-loan/what-you-pay"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>
              <li>
                Writing off (all plan types):
                <a
                  href="https://www.gov.uk/repaying-your-student-loan/when-your-student-loan-gets-written-off-or-cancelled"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source
                </a>
              </li>
            </ul>

            <h3>Plan-Specific Information</h3>
            <ul>
              {PLAN_INFORMATION.map((plan) => (
                <li key={plan.key}>
                  <strong>{LOAN_PLAN_LABELS[plan.key]}</strong>: <br></br>- Payment is {plan.paymentRate} of your income above
                  the threshold of {plan.threshold}. <br></br>- Balances are {plan.assumedWipe ? "assumed to wipe" : "wiped"} {plan.writeOff} after the April you were
                  first due to repay. <br></br>
                  {plan.interestNote ? (
                    <>
                      - Interest rate {formatInterestNote(plan.interestNote)}{" "}
                      <a href={plan.interestUrl} target="_blank" rel="noopener noreferrer">
                        Source
                      </a>
                    </>
                  ) : (
                    <>
                      - Interest rate.
                      {" "}
                      <a href={plan.interestUrl} target="_blank" rel="noopener noreferrer">
                        Source
                      </a>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </details>
      </div>

      <div className="support-panel">
        <h2 className="support-title">Enjoying SLAM?</h2>
        <p className="support-copy">
          SLAM is created and maintained by an individual developer and is available completely free!<br></br>
          If you find the tool helpful, please consider supporting the project or leaving feedback to help make it even better.
        </p>
        <a
          className="support-button"
          href="https://buymeacoffee.com/robertking1998"
          target="_blank"
          rel="noopener noreferrer"
        >
          Support SLAM
        </a>
      </div>

      <SiteFooter onOpenCookieSettings={openCookieSettings} />

      <CookieConsentBanner />
      {!isDisclaimerAccepted && <DisclaimerModal onAccept={handleDisclaimerAccept} />}
    </div>
  );
}

export default App;
